/**
 * MapSelectScene — Phaser Scene for the map selection screen.
 *
 * Displays all maps as large illustrated cards with:
 * - Map name
 * - Preview illustration
 * - Progress indicator (X/5 caves completed)
 * - Lock status (padlock overlay + reduced opacity for locked maps)
 *
 * Interactions:
 * - Tap unlocked card → load and enter map overworld (GameScene)
 * - Tap locked card → shake animation + locked sound feedback
 * - Hover/long-press → TTS narration of map name via AudioCoordinator
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5, 5.4
 */

import Phaser from 'phaser';
import type { MapCard } from '../types/progress.types';
import type { MapManager } from '../managers/MapManager';
import type { IAudioCoordinator } from '../systems/AudioCoordinator';

/** Scene data passed when starting MapSelectScene */
export interface MapSelectSceneData {
  mapManager: MapManager;
  audioCoordinator: IAudioCoordinator;
}

/** Constants for card layout and styling */
const CARD_WIDTH = 280;
const CARD_HEIGHT = 360;
const CARD_PADDING = 40;
const CARD_CORNER_RADIUS = 16;
const CARD_BG_COLOR = 0xffffff;
const CARD_BORDER_COLOR = 0x4a90d9;
const CARD_LOCKED_OPACITY = 0.5;
const PROGRESS_BAR_HEIGHT = 12;
const PROGRESS_BAR_BG_COLOR = 0xdddddd;
const PROGRESS_BAR_FILL_COLOR = 0x4caf50;
const PADLOCK_SIZE = 64;
const SHAKE_DURATION = 400;
const SHAKE_INTENSITY = 4;
const LONG_PRESS_DELAY = 600;
const TITLE_FONT_SIZE = '20px';
const PROGRESS_FONT_SIZE = '16px';
const HEADER_FONT_SIZE = '32px';

export class MapSelectScene extends Phaser.Scene {
  private mapManager!: MapManager;
  private audioCoordinator!: IAudioCoordinator;
  private cardContainers: Phaser.GameObjects.Container[] = [];
  private longPressTimers: Map<string, Phaser.Time.TimerEvent> = new Map();

  constructor() {
    super({ key: 'MapSelectScene' });
  }

  init(data?: MapSelectSceneData): void {
    if (data?.mapManager) {
      this.mapManager = data.mapManager;
    }
    if (data?.audioCoordinator) {
      this.audioCoordinator = data.audioCoordinator;
    }
  }

  preload(): void {
    // Load map select background image if not already loaded
    if (!this.textures.exists('map-select-bg')) {
      this.load.image('map-select-bg', 'assets/adventure/map-select-bg.png');
    }
  }

  create(): void {
    // Clear any previous card containers
    this.cardContainers = [];
    this.longPressTimers.clear();

    // Check if all 4 worlds are complete → transition to FinalConclusionScene
    if (this.checkAllWorldsComplete()) {
      this.scene.start('FinalConclusionScene', { conclusionImageKey: 'conclusao-final' });
      return;
    }

    const { width, height } = this.scale;

    // Background: use illustrated image if available, otherwise fallback to solid color
    if (this.textures.exists('map-select-bg') && this.textures.get('map-select-bg').key !== '__MISSING') {
      const bg = this.add.image(width / 2, height / 2, 'map-select-bg');
      // Scale to fill viewport while maintaining aspect ratio (cover behavior)
      const scaleX = width / bg.width;
      const scaleY = height / bg.height;
      const scale = Math.max(scaleX, scaleY);
      bg.setScale(scale);
      bg.setDepth(0);
    } else {
      // Fallback: solid color background if image is missing
      this.add.rectangle(width / 2, height / 2, width, height, 0x1a1a2e).setDepth(0);
    }

    // Header title
    this.add
      .text(width / 2, 50, 'Escolhe o teu Mundo!', {
        fontFamily: 'Arial, sans-serif',
        fontSize: HEADER_FONT_SIZE,
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(1);

    // Settings button (top-right) — opens CustomizationScene
    const settingsBtn = this.add
      .text(width - 50, 40, '⚙️', {
        fontSize: '32px',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    settingsBtn.on('pointerdown', () => {
      this.scene.start('CustomizationScene', { fromSettings: true });
    });

    settingsBtn.on('pointerover', () => {
      settingsBtn.setScale(1.2);
    });

    settingsBtn.on('pointerout', () => {
      settingsBtn.setScale(1.0);
    });

    // Reset button (top-left) — clears progress and restarts
    const resetBtn = this.add
      .text(50, 40, '🔄', {
        fontSize: '28px',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    resetBtn.on('pointerdown', () => {
      this.showResetConfirmation(width, height);
    });

    resetBtn.on('pointerover', () => {
      resetBtn.setScale(1.2);
    });

    resetBtn.on('pointerout', () => {
      resetBtn.setScale(1.0);
    });

    // Get map cards — use MapManager if available, otherwise show demo cards
    const mapCards: MapCard[] = this.mapManager
      ? this.mapManager.getMapCards()
      : this.getDemoMapCards();

    if (mapCards.length === 0) {
      this.add
        .text(width / 2, height / 2, 'Nenhum mapa disponível', {
          fontFamily: 'Arial, sans-serif',
          fontSize: '24px',
          color: '#cccccc',
        })
        .setOrigin(0.5, 0.5)
        .setDepth(1);
      return;
    }

    // Calculate card positions (centered horizontally)
    const totalCardsWidth =
      mapCards.length * CARD_WIDTH + (mapCards.length - 1) * CARD_PADDING;
    const startX = (width - totalCardsWidth) / 2 + CARD_WIDTH / 2;
    const cardY = height / 2 + 20;

    // Create each map card
    mapCards.forEach((card, index) => {
      const x = startX + index * (CARD_WIDTH + CARD_PADDING);
      const container = this.createMapCard(card, index, x, cardY);
      this.cardContainers.push(container);
    });
  }

  /**
   * Creates a single map card container with all visual elements and input handlers.
   */
  private createMapCard(
    card: MapCard,
    index: number,
    x: number,
    y: number
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y).setDepth(10);

    // Card background with rounded corners (using graphics)
    const bg = this.add.graphics();
    bg.fillStyle(CARD_BG_COLOR, 1);
    bg.fillRoundedRect(
      -CARD_WIDTH / 2,
      -CARD_HEIGHT / 2,
      CARD_WIDTH,
      CARD_HEIGHT,
      CARD_CORNER_RADIUS
    );
    bg.lineStyle(3, CARD_BORDER_COLOR, 1);
    bg.strokeRoundedRect(
      -CARD_WIDTH / 2,
      -CARD_HEIGHT / 2,
      CARD_WIDTH,
      CARD_HEIGHT,
      CARD_CORNER_RADIUS
    );
    container.add(bg);

    // Preview image area (placeholder rectangle if texture not loaded)
    const previewY = -CARD_HEIGHT / 2 + 20;
    const previewWidth = CARD_WIDTH - 30;
    const previewHeight = 160;

    if (card.previewImage && this.textures.exists(card.previewImage)) {
      const preview = this.add
        .image(0, previewY + previewHeight / 2, card.previewImage)
        .setDisplaySize(previewWidth, previewHeight);
      container.add(preview);
    } else {
      // Placeholder colored rectangle based on theme
      const themeColors: Record<string, number> = {
        forest: 0x228b22,
        ice: 0x87ceeb,
        mountain: 0x6b8e9f,
        castle: 0x666680,
        space: 0x1a1a3e,
      };
      const placeholderColor = themeColors[card.theme] ?? 0x666666;
      const placeholder = this.add.graphics();
      placeholder.fillStyle(placeholderColor, 0.7);
      placeholder.fillRoundedRect(
        -previewWidth / 2,
        previewY,
        previewWidth,
        previewHeight,
        8
      );
      container.add(placeholder);

      // Theme icon text in placeholder
      const themeIcons: Record<string, string> = {
        forest: '🌲',
        ice: '❄️',
        mountain: '⛰️',
        castle: '🏰',
        space: '🚀',
      };
      const iconText = this.add
        .text(0, previewY + previewHeight / 2, themeIcons[card.theme] ?? '🗺️', {
          fontSize: '48px',
        })
        .setOrigin(0.5, 0.5);
      container.add(iconText);
    }

    // Map name
    const nameY = previewY + previewHeight + 25;
    const nameText = this.add
      .text(0, nameY, card.name, {
        fontFamily: 'Arial, sans-serif',
        fontSize: TITLE_FONT_SIZE,
        color: '#333333',
        fontStyle: 'bold',
        wordWrap: { width: CARD_WIDTH - 30 },
        align: 'center',
      })
      .setOrigin(0.5, 0.5);
    container.add(nameText);

    // Progress indicator (X/5 caves)
    const progressY = nameY + 35;
    const progressText = this.add
      .text(0, progressY, `${card.cavesCompleted}/${card.totalCaves} grutas`, {
        fontFamily: 'Arial, sans-serif',
        fontSize: PROGRESS_FONT_SIZE,
        color: '#666666',
      })
      .setOrigin(0.5, 0.5);
    container.add(progressText);

    // Progress bar
    const barY = progressY + 20;
    const barWidth = CARD_WIDTH - 60;
    const progressBarBg = this.add.graphics();
    progressBarBg.fillStyle(PROGRESS_BAR_BG_COLOR, 1);
    progressBarBg.fillRoundedRect(
      -barWidth / 2,
      barY,
      barWidth,
      PROGRESS_BAR_HEIGHT,
      PROGRESS_BAR_HEIGHT / 2
    );
    container.add(progressBarBg);

    // Progress bar fill
    const fillRatio =
      card.totalCaves > 0 ? card.cavesCompleted / card.totalCaves : 0;
    if (fillRatio > 0) {
      const progressBarFill = this.add.graphics();
      progressBarFill.fillStyle(PROGRESS_BAR_FILL_COLOR, 1);
      const fillWidth = Math.max(PROGRESS_BAR_HEIGHT, barWidth * fillRatio);
      progressBarFill.fillRoundedRect(
        -barWidth / 2,
        barY,
        fillWidth,
        PROGRESS_BAR_HEIGHT,
        PROGRESS_BAR_HEIGHT / 2
      );
      container.add(progressBarFill);
    }

    // Locked state: padlock overlay + reduced opacity
    if (!card.isUnlocked) {
      container.setAlpha(CARD_LOCKED_OPACITY);

      // Padlock overlay
      const padlockBg = this.add.graphics();
      padlockBg.fillStyle(0x000000, 0.4);
      padlockBg.fillRoundedRect(
        -CARD_WIDTH / 2,
        -CARD_HEIGHT / 2,
        CARD_WIDTH,
        CARD_HEIGHT,
        CARD_CORNER_RADIUS
      );
      container.add(padlockBg);

      const padlockText = this.add
        .text(0, 0, '🔒', {
          fontSize: `${PADLOCK_SIZE}px`,
        })
        .setOrigin(0.5, 0.5);
      container.add(padlockText);
    }

    // Interactive hit area for the card
    const hitArea = this.add
      .rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, 0xffffff, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hitArea);

    // Input events
    this.setupCardInput(hitArea, card, container);

    return container;
  }

  /**
   * Set up input event handlers for a map card.
   */
  private setupCardInput(
    hitArea: Phaser.GameObjects.Rectangle,
    card: MapCard,
    container: Phaser.GameObjects.Container
  ): void {
    // Tap / click
    hitArea.on('pointerdown', () => {
      this.handleCardTap(card, container);

      // Start long-press timer for TTS
      const timer = this.time.delayedCall(LONG_PRESS_DELAY, () => {
        this.narrateMapName(card.name);
      });
      this.longPressTimers.set(card.id, timer);
    });

    hitArea.on('pointerup', () => {
      this.cancelLongPress(card.id);
    });

    hitArea.on('pointerout', () => {
      this.cancelLongPress(card.id);
    });

    // Hover (desktop) — narrate map name via TTS
    hitArea.on('pointerover', () => {
      this.narrateMapName(card.name);

      // Subtle scale-up on hover for unlocked cards
      if (card.isUnlocked) {
        this.tweens.add({
          targets: container,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 150,
          ease: 'Power2',
        });
      }
    });

    hitArea.on('pointerout', () => {
      // Reset scale on hover out
      this.tweens.add({
        targets: container,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Power2',
      });
    });
  }

  /**
   * Handle tap on a map card.
   * Unlocked → transition to GameScene with map data.
   * Locked → shake animation + locked sound feedback.
   */
  private handleCardTap(card: MapCard, container: Phaser.GameObjects.Container): void {
    if (card.isUnlocked) {
      // Transition to GameScene with selected map
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GameScene', { mapId: card.id });
      });
    } else {
      // Locked card: shake animation + locked sound
      this.playShakeAnimation(container);
      this.playLockedSound();
    }
  }

  /**
   * Play a shake animation on a locked card to indicate it's not accessible.
   */
  private playShakeAnimation(container: Phaser.GameObjects.Container): void {
    const originalX = container.x;

    this.tweens.add({
      targets: container,
      x: {
        value: originalX + SHAKE_INTENSITY,
        duration: SHAKE_DURATION / 8,
        ease: 'Sine.inOut',
        yoyo: true,
        repeat: 3,
      },
      onComplete: () => {
        container.x = originalX;
      },
    });
  }

  /**
   * Play a locked sound effect to indicate the map is not accessible.
   */
  private playLockedSound(): void {
    try {
      this.audioCoordinator?.playSoundEffect('audio/locked.mp3');
    } catch {
      // Graceful degradation: if audio fails, the shake animation still provides feedback
    }
  }

  /**
   * Narrate the map name via TTS using AudioCoordinator.
   */
  private narrateMapName(name: string): void {
    try {
      this.audioCoordinator?.speakTTS(name);
    } catch {
      // Graceful degradation: if TTS unavailable, game continues without narration
    }
  }

  /**
   * Cancel a long-press timer for a card.
   */
  private cancelLongPress(cardId: string): void {
    const timer = this.longPressTimers.get(cardId);
    if (timer) {
      timer.destroy();
      this.longPressTimers.delete(cardId);
    }
  }

  /**
   * Shows a confirmation dialog to reset all progress.
   */
  private showResetConfirmation(width: number, height: number): void {
    // Overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7).setDepth(50);
    
    // Dialog box
    const dialogBg = this.add.graphics().setDepth(51);
    const dw = 400, dh = 200;
    dialogBg.fillStyle(0x2a2a4e, 1);
    dialogBg.fillRoundedRect(width / 2 - dw / 2, height / 2 - dh / 2, dw, dh, 16);
    dialogBg.lineStyle(2, 0xffd700, 1);
    dialogBg.strokeRoundedRect(width / 2 - dw / 2, height / 2 - dh / 2, dw, dh, 16);

    // Text
    const dialogText = this.add.text(width / 2, height / 2 - 40, 'Recomeçar do início?', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5).setDepth(52);

    const subText = this.add.text(width / 2, height / 2 - 10, 'O progresso será apagado.', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#cccccc',
      align: 'center',
    }).setOrigin(0.5).setDepth(52);

    // Confirm button
    const confirmBg = this.add.graphics().setDepth(52);
    confirmBg.fillStyle(0xdc3545, 1);
    confirmBg.fillRoundedRect(width / 2 - 150, height / 2 + 30, 130, 44, 10);
    const confirmText = this.add.text(width / 2 - 85, height / 2 + 52, 'Sim', {
      fontFamily: 'Arial, sans-serif', fontSize: '18px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(53);
    const confirmHit = this.add.rectangle(width / 2 - 85, height / 2 + 52, 130, 44, 0xffffff, 0)
      .setInteractive({ useHandCursor: true }).setDepth(53);

    // Cancel button
    const cancelBg = this.add.graphics().setDepth(52);
    cancelBg.fillStyle(0x6c757d, 1);
    cancelBg.fillRoundedRect(width / 2 + 20, height / 2 + 30, 130, 44, 10);
    const cancelText = this.add.text(width / 2 + 85, height / 2 + 52, 'Não', {
      fontFamily: 'Arial, sans-serif', fontSize: '18px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(53);
    const cancelHit = this.add.rectangle(width / 2 + 85, height / 2 + 52, 130, 44, 0xffffff, 0)
      .setInteractive({ useHandCursor: true }).setDepth(53);

    const cleanup = () => {
      overlay.destroy();
      dialogBg.destroy();
      dialogText.destroy();
      subText.destroy();
      confirmBg.destroy();
      confirmText.destroy();
      confirmHit.destroy();
      cancelBg.destroy();
      cancelText.destroy();
      cancelHit.destroy();
    };

    confirmHit.on('pointerdown', () => {
      // Clear all progress
      try {
        localStorage.removeItem('awe_progress_default');
        localStorage.removeItem('awe_progress_montanha-gelada');
        localStorage.removeItem('awe_progress_reino-encantado');
        localStorage.removeItem('awe_progress_espaco-sideral');
      } catch { /* ignore */ }
      cleanup();
      // Restart scene
      this.scene.restart();
    });

    cancelHit.on('pointerdown', () => {
      cleanup();
    });
  }

  /**
   * Returns demo map cards for development/testing when no MapManager is available.
   * Checks localStorage progress to determine unlock status for each world.
   * Progression: Floresta → Montanha → Reino → Espaço
   */
  private getDemoMapCards(): MapCard[] {
    // Check progress from localStorage
    let florestaCompleted = 0;
    let montanhaCompleted = 0;
    let reinoCompleted = 0;
    let espacoCompleted = 0;
    try {
      const saved = localStorage.getItem('awe_progress_default');
      if (saved) {
        const data = JSON.parse(saved);
        const completedCaves: string[] = data.completedCaves || [];
        florestaCompleted = completedCaves.length;
      }
      const savedMontanha = localStorage.getItem('awe_progress_montanha-gelada');
      if (savedMontanha) {
        const data = JSON.parse(savedMontanha);
        const completedCaves: string[] = data.completedCaves || [];
        montanhaCompleted = completedCaves.length;
      }
      const savedReino = localStorage.getItem('awe_progress_reino-encantado');
      if (savedReino) {
        const data = JSON.parse(savedReino);
        const completedCaves: string[] = data.completedCaves || [];
        reinoCompleted = completedCaves.length;
      }
      const savedEspaco = localStorage.getItem('awe_progress_espaco-sideral');
      if (savedEspaco) {
        const data = JSON.parse(savedEspaco);
        const completedCaves: string[] = data.completedCaves || [];
        espacoCompleted = completedCaves.length;
      }
    } catch {
      // Ignore parse errors
    }

    // Unlock conditions: each world unlocks when previous world has 5/5
    const montanhaUnlocked = florestaCompleted >= 5;
    const reinoUnlocked = montanhaCompleted >= 5;
    const espacoUnlocked = reinoCompleted >= 5;

    return [
      {
        id: 'floresta-encantada',
        name: 'Floresta Encantada',
        theme: 'forest',
        previewImage: 'floresta-preview',
        cavesCompleted: florestaCompleted,
        totalCaves: 5,
        isUnlocked: true,
      },
      {
        id: 'montanha-gelada',
        name: 'Montanha Gelada',
        theme: 'ice',
        previewImage: 'montanha-preview',
        cavesCompleted: montanhaCompleted,
        totalCaves: 5,
        isUnlocked: montanhaUnlocked,
      },
      {
        id: 'reino-encantado',
        name: 'Reino Encantado',
        theme: 'castle',
        previewImage: 'reino-preview',
        cavesCompleted: reinoCompleted,
        totalCaves: 5,
        isUnlocked: reinoUnlocked,
      },
      {
        id: 'espaco-sideral',
        name: 'Espaço Sideral',
        theme: 'space',
        previewImage: 'espaco-preview',
        cavesCompleted: espacoCompleted,
        totalCaves: 5,
        isUnlocked: espacoUnlocked,
      },
    ];
  }

  /**
   * Check if all 4 worlds have been completed by reading the awe_completed_worlds localStorage key.
   * Returns true if all 4 world IDs are present in the completed worlds array.
   */
  private checkAllWorldsComplete(): boolean {
    const allWorldIds = [
      'floresta-encantada',
      'montanha-gelada',
      'reino-encantado',
      'espaco-sideral',
    ];

    try {
      const raw = localStorage.getItem('awe_completed_worlds');
      if (!raw) {
        return false;
      }
      const completedWorlds: string[] = JSON.parse(raw);
      if (!Array.isArray(completedWorlds)) {
        return false;
      }
      return allWorldIds.every((id) => completedWorlds.includes(id));
    } catch {
      return false;
    }
  }
}
